import React, { useMemo, useCallback } from "react";
import { Box, Typography, Select, MenuItem, FormControl } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  TooltipProps,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { SourcingCycleTimeData } from "../../../types/contractDashboard";
import styles from "./sourcingCycleTime.module.scss";

interface SourcingCycleTimeProps {
  data: SourcingCycleTimeData[];
  targetDays?: number;
}

// Custom label component for reference line
const CustomReferenceLabel = (props: any) => {
  const { viewBox, value } = props;
  const { x, y, width } = viewBox;
  const cx = x + width / 2;

  return (
    <g>
      <rect
        x={cx - 60}
        y={y - 12}
        width={120}
        height={24}
        fill="white"
        stroke="#e5e7eb"
        strokeWidth={1}
        rx={6}
        ry={6}
      />
      <text
        x={cx}
        y={y}
        fill="#64748b"
        fontSize={12}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {value}
      </text>
    </g>
  );
};

// Custom tooltip component for better UX
const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const value = payload[0].value as number;
  const targetValue = payload[0].payload.target as number | undefined;

  return (
    <Box
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, marginBottom: "8px", color: "#1a1a1a" }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: targetValue ? "4px" : 0,
        }}
      >
        <Box
          sx={{
            width: "12px",
            height: "12px",
            borderRadius: "2px",
            backgroundColor: payload[0].color,
          }}
        />
        <Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
          Cycle Time:
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "#1a1a1a", fontSize: "13px" }}
        >
          {value} days
        </Typography>
      </Box>
      {targetValue !== undefined && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            paddingTop: "4px",
            borderTop: "1px solid #f0f0f0",
            marginTop: "4px",
          }}
        >
          <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
            Target: {targetValue} days
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: value > targetValue ? "#ef4444" : "#10b981",
              fontSize: "12px",
            }}
          >
            ({value > targetValue ? "+" : ""}
            {value - targetValue} days)
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const SourcingCycleTime: React.FC<SourcingCycleTimeProps> = ({
  data,
  targetDays = 35,
}) => {
  // Memoize chart data with target information
  const chartData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      target: targetDays,
    }));
  }, [data, targetDays]);

  // Memoize Y-axis domain calculation
  const yAxisDomain = useMemo(() => {
    if (!chartData || chartData.length === 0) return [0, 50];
    const maxValue = Math.max(...chartData.map((d) => d.cycleTimeDays));
    return [0, Math.ceil(maxValue * 1.15)];
  }, [chartData]);

  // Memoize Y-axis tick formatter
  const yAxisTickFormatter = useCallback((value: number) => {
    return `${value}d`;
  }, []);


  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Box className={styles.titleSection}>
            <Typography variant="h6" className={styles.title}>
              Sourcing Cycle Time
            </Typography>
            <Typography variant="body2" className={styles.subtitle}>
              RFx launch to award (days).
            </Typography>
          </Box>
        </Box>
        <Box className={styles.chartPlaceholder}>
          <Typography variant="body2" className={styles.placeholderText}>
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Sourcing Cycle Time
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            RFx launch to award (days).
          </Typography>
        </Box>
        <FormControl className={styles.filter}>
          <Select
            defaultValue="Quarterly"
            IconComponent={KeyboardArrowDownIcon}
            className={styles.select}
            aria-label="Time period filter"
          >
            <MenuItem value="Quarterly">Quarterly</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box className={styles.chartPlaceholder}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tickFormatter={yAxisTickFormatter}
              domain={yAxisDomain}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
              label={{
                value: "Days",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 12, fill: "#6b7280" },
              }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0, 0, 0, 0.05)" }} />
            <ReferenceLine
              y={targetDays}
              stroke="#94a3b8"
              strokeDasharray="5 5"
              strokeWidth={2}
              label={<CustomReferenceLabel value={`Target: ${targetDays} days`} />}
            />
            <Bar
              dataKey="cycleTimeDays"
              name="Cycle Time (Days)"
              fill="#00d2aa"
              animationDuration={800}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(SourcingCycleTime);

